import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Play, MapPin, UserCheck, CheckCircle2, Timer, AlertTriangle, CloudOff, Undo2, Navigation } from "lucide-react";
import { toast } from "sonner";

export const STEP_META = {
  departed: { label: "Partito verso il pickup", icon: Navigation },
  arrived: { label: "Arrivato sul posto", icon: MapPin },
  onboard: { label: "Cliente a bordo", icon: UserCheck },
  started: { label: "Servizio iniziato", icon: Play },
  completed: { label: "Servizio completato", icon: CheckCircle2 },
};
export const STEP_ORDER = ["departed", "arrived", "onboard", "started", "completed"];
export const WAIT_REASONS = { volo_ritardo: "Ritardo del volo", cliente_assente: "Cliente non ancora presente", richiesta_cliente: "Attesa richiesta dal cliente", durante_servizio: "Attesa durante il trasferimento", altro: "Altro" };
export const INCIDENTS = { ritardo: "Ritardo", cliente_assente: "Cliente assente", volo_ritardo: "Volo in ritardo", traffico: "Traffico intenso", variazione_cliente: "Variazione dal cliente", altro: "Altro imprevisto" };
export const hhmm = (iso) => iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "--:--";
export const dur = (s) => { if (s == null) return "—"; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60; return h ? `${h}h ${String(m).padStart(2, "0")}min` : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`; };
const cid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const qKey = (id) => `sp-queue-${id}`;
const readQ = (id) => { try { return JSON.parse(localStorage.getItem(qKey(id))) || []; } catch { return []; } };
const writeQ = (id, q) => { if (q.length) localStorage.setItem(qKey(id), JSON.stringify(q)); else localStorage.removeItem(qKey(id)); };

export function useTick(active) {
  const [, setT] = useState(0);
  useEffect(() => { if (!active) return undefined; const i = setInterval(() => setT(t => t + 1), 1000); return () => clearInterval(i); }, [active]);
}

export function Timeline({ p }) {
  return (
    <ol className="space-y-1.5" data-testid="sp-timeline">
      {STEP_ORDER.map((t, i) => {
        const s = p.steps[t]; const M = STEP_META[t];
        return (
          <li key={t} className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${s ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800"}`} data-testid={`sp-step-${t}`}>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${s ? "bg-gradient-to-b from-[#F1D27A] to-[#C99A24] text-black" : "border border-slate-700 text-slate-500"}`}>{s ? "✓" : i + 1}</span>
            <span className={`flex-1 text-sm ${s ? "text-white" : "text-slate-500"}`}>{M.label}{s?.note ? <span className="block text-[11px] text-slate-400">{s.note}</span> : null}</span>
            <span className="font-mono-num text-sm text-amber-300">{s ? hhmm(s.at) : "--:--"}</span>
          </li>
        );
      })}
      {p.waits.map(w => (
        <li key={w.start_event_id} className="flex items-start gap-3 rounded-xl border border-sky-500/30 px-3 py-2 text-sm" data-testid="sp-wait-row">
          <Timer size={16} className="text-sky-300 mt-0.5 shrink-0" />
          <span className="flex-1 min-w-0 text-slate-300">Attesa · {w.reason_label}{w.note ? ` · ${w.note}` : ""}
            <span className="block font-mono-num text-sky-300 text-xs">{hhmm(w.start)}–{w.end ? hhmm(w.end) : "in corso"} · {dur(w.end ? w.seconds : Math.max(0, Math.floor((Date.now() - new Date(w.start)) / 1000)))}</span></span>
        </li>
      ))}
      {p.incidents.map(x => (
        <li key={x.event_id} className="flex items-center gap-3 rounded-xl border border-rose-500/40 px-3 py-2 text-sm" data-testid="sp-incident-row">
          <AlertTriangle size={16} className="text-rose-300" /><span className="flex-1 text-rose-200">{x.label}{x.note ? `: ${x.note}` : ""}</span><span className="font-mono-num text-rose-300">{hhmm(x.at)}</span>
        </li>
      ))}
    </ol>
  );
}

const bigBtn = "w-full min-h-[52px] rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50";
const gold = `${bigBtn} text-black bg-gradient-to-b from-[#F1D27A] to-[#C99A24]`;
const outline = `${bigBtn} border border-amber-500/50 text-amber-200`;

export function ServiceProgressPanel({ bookingId }) {
  const [p, setP] = useState(null);
  const [queue, setQueue] = useState(() => readQ(bookingId));
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);
  const [form, setForm] = useState({ reason: "", note: "" });
  useTick(!!p?.open_wait);
  const load = useCallback(async () => {
    try { setP((await api.get(`/service-progress/booking/${bookingId}`)).data); } catch (e) { setP(x => x || { error: formatApiError(e) }); }
  }, [bookingId]);

  const flush = useCallback(async () => {
    let q = readQ(bookingId);
    for (const item of q) {
      try {
        const r = await api.post(`/service-progress/booking/${bookingId}/events`, item, { timeout: 20000 });
        setP(r.data);
        q = q.filter(x => x.client_id !== item.client_id);
      } catch (e) {
        if (e?.response) { toast.error(formatApiError(e)); q = q.filter(x => x.client_id !== item.client_id); }
        else break;
      }
    }
    writeQ(bookingId, q); setQueue(q);
  }, [bookingId]);

  useEffect(() => { load(); flush(); const on = () => flush(); window.addEventListener("online", on); return () => window.removeEventListener("online", on); }, [load, flush]);

  const send = async (type, extra = {}) => {
    const item = { type, client_id: cid(), occurred_at: new Date().toISOString(), ...extra };
    const q = [...readQ(bookingId), item]; writeQ(bookingId, q); setQueue(q);
    setBusy(true); await flush(); setBusy(false); setMode(null); setForm({ reason: "", note: "" });
    if (readQ(bookingId).some(x => x.client_id === item.client_id)) toast.warning("Connessione assente: aggiornamento in attesa di conferma, verrà reinviato");
  };
  const undo = async (eventId) => {
    const note = window.prompt("Motivo della correzione (resta nello storico):");
    if (!note) return;
    try { setP((await api.post(`/service-progress/booking/${bookingId}/corrections`, { event_id: eventId, action: "void", note })).data); toast.success("Correzione registrata"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!p) return <div className="text-sm text-slate-500">Caricamento avanzamento...</div>;
  if (p.error) return <div className="text-sm text-rose-300" data-testid="sp-error">{p.error}</div>;
  const done = p.state === "completed";
  const rank = STEP_ORDER.indexOf(p.state);
  const next = STEP_ORDER.filter((t, i) => i > rank && !(t === "completed" && rank < 2));
  const lastStep = [...STEP_ORDER].reverse().find(t => p.steps[t]);
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-b from-[#0F0D09] to-[#070605] p-4 space-y-3" data-testid="service-progress-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold">Avanzamento servizio</div>
        {done && <span className="text-xs text-emerald-300" data-testid="sp-completed-badge">Completato{p.total_seconds != null ? ` · ${dur(p.total_seconds)}` : ""}</span>}
      </div>
      {queue.length > 0 && (
        <button type="button" onClick={flush} className="w-full rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-200 text-xs p-2 flex items-center gap-2" data-testid="sp-pending">
          <CloudOff size={14} />{queue.length} aggiornament{queue.length === 1 ? "o" : "i"} in attesa di conferma — tocca per reinviare
        </button>
      )}
      {p.open_wait && (
        <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 p-3 text-center" data-testid="sp-open-wait">
          <div className="text-sky-200 text-sm">Attesa in corso · {WAIT_REASONS[p.open_wait.reason] || "Attesa"}</div>
          <div className="font-mono-num text-3xl text-white">{dur(Math.max(0, Math.floor((Date.now() - new Date(p.open_wait.start)) / 1000)))}</div>
          <div className="text-[11px] text-slate-400">Iniziata alle {hhmm(p.open_wait.start)}</div>
        </div>
      )}
      <Timeline p={p} />
      {!done && mode === null && (
        <div className="space-y-2">
          {p.open_wait ? (
            <button type="button" disabled={busy} className={gold} onClick={() => setMode("wait_end")} data-testid="sp-wait-end-btn"><Timer size={18} />Termina attesa</button>
          ) : next.map((t, i) => {
            const M = STEP_META[t];
            return <button key={t} type="button" disabled={busy} className={i === 0 ? gold : outline} onClick={() => send(t)} data-testid={`sp-btn-${t}`}><M.icon size={18} />{M.label}</button>;
          })}
          <div className="grid grid-cols-2 gap-2">
            {!p.open_wait && rank >= 0 && <button type="button" disabled={busy} className={outline} onClick={() => setMode("wait_start")} data-testid="sp-wait-start-btn"><Timer size={16} />Inizia attesa</button>}
            <button type="button" disabled={busy} className={`${bigBtn} border border-rose-500/50 text-rose-200 ${!p.open_wait && rank >= 0 ? "" : "col-span-2"}`} onClick={() => setMode("incident")} data-testid="sp-incident-btn"><AlertTriangle size={16} />Segnala imprevisto</button>
          </div>
        </div>
      )}
      {mode && (
        <div className="rounded-xl border border-slate-800 p-3 space-y-2" data-testid={`sp-form-${mode}`}>
          {mode !== "wait_end" && (
            <select value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="w-full h-11 rounded-lg bg-slate-950 border border-slate-800 text-sm text-slate-200 px-2" data-testid="sp-reason">
              <option value="">{mode === "incident" ? "Tipo di imprevisto" : "Motivo dell'attesa"}</option>
              {Object.entries(mode === "incident" ? INCIDENTS : WAIT_REASONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
          <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Note (facoltative)" className="w-full rounded-lg bg-slate-950 border border-slate-800 text-sm text-slate-200 p-2" data-testid="sp-note" />
          {mode !== "incident" && <p className="text-[11px] text-slate-500">La durata dell'attesa viene registrata nel servizio. L'eventuale addebito dipende dalle condizioni concordate con il cliente.</p>}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className={outline} onClick={() => setMode(null)} data-testid="sp-form-cancel">Annulla</button>
            <button type="button" disabled={busy} className={gold} data-testid="sp-form-confirm" onClick={() => {
              if (mode === "incident" && !form.reason) { toast.error("Scegli il tipo di imprevisto"); return; }
              send(mode, { reason: form.reason || (mode === "wait_start" ? "altro" : undefined), note: form.note });
            }}>Conferma</button>
          </div>
        </div>
      )}
      {lastStep && !busy && (
        <button type="button" onClick={() => undo(p.steps[lastStep].event_id)} className="text-[11px] text-slate-500 hover:text-amber-300 flex items-center gap-1" data-testid="sp-undo-btn"><Undo2 size={12} />Correggi «{STEP_META[lastStep].label}» registrato per errore</button>
      )}
    </div>
  );
}
